import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Badge, Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { PickersDay } from '@mui/x-date-pickers/PickersDay'
import { common, orange } from '@mui/material/colors'
import WarningRoundedIcon from '@mui/icons-material/WarningRounded'
import { HEATMAP_VALUE_TO_RECURRENCE, RECURRENCE_TO_HEATMAP_VALUE } from 'settings/constants/workOrder'
import { formatDateToBEFormatDateFns } from 'utils/date'

const StyledPickersDay = styled(PickersDay, {
  shouldForwardProp: prop => prop !== 'heatmapColor',
})(({ theme, heatmapColor }) => {
  const isEmpty = !heatmapColor

  return {
    borderRadius: '50%',
    backgroundColor: heatmapColor || 'transparent',
    border: 'none',
    '&.MuiPickersDay-today': {
      border: `1px solid ${theme.palette.divider}`,
    },
    '&:hover': {
      backgroundColor: isEmpty ? theme.palette.action.hover : heatmapColor,
      opacity: 0.85,
    },
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: common.white,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  }
})

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: 4,
    top: 8,
    minWidth: 14,
    height: 14,
    fontSize: '0.5625rem',
    fontWeight: 400,
    padding: '0 3px',
    backgroundColor: theme.palette.primary.main,
    color: common.white,
    border: `1.5px solid white`,
  },
}))

const HHDateCalendarDay = props => {
  const { heatmap = {}, dayMetaByDate = {}, day, outsideCurrentMonth, ...other } = props

  const dateStr = useMemo(() => formatDateToBEFormatDateFns(day), [day])
  const dayData = heatmap[dateStr]

  const heatmapValue = useMemo(() => {
    if (!dayData) return RECURRENCE_TO_HEATMAP_VALUE.EMPTY
    const { recurringCount = 0, requestCount = 0 } = dayData
    if (recurringCount > 0 && requestCount > 0) return RECURRENCE_TO_HEATMAP_VALUE.MIXED
    if (recurringCount > 0) return RECURRENCE_TO_HEATMAP_VALUE.RECURRING
    if (requestCount > 0) return RECURRENCE_TO_HEATMAP_VALUE.ON_REQUEST
    return RECURRENCE_TO_HEATMAP_VALUE.EMPTY
  }, [dayData])
  const heatmapColor = useMemo(() => {
    if (heatmapValue === RECURRENCE_TO_HEATMAP_VALUE.EMPTY) return undefined
    return HEATMAP_VALUE_TO_RECURRENCE[heatmapValue]?.color
  }, [heatmapValue])

  const woCount = useMemo(() => {
    if (!dayData) return 0
    return (dayData.recurringCount || 0) + (dayData.requestCount || 0)
  }, [dayData])

  const dayMeta = dayMetaByDate[dateStr]
  const hasUnassigned = dayMeta?.hasUnassignedRoute

  const showBadge = !outsideCurrentMonth && woCount > 0

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <StyledBadge overlap="circular" badgeContent={showBadge ? woCount : undefined}>
        <StyledPickersDay {...other} day={day} outsideCurrentMonth={outsideCurrentMonth} heatmapColor={heatmapColor} />
      </StyledBadge>

      {!outsideCurrentMonth && hasUnassigned && (
        <WarningRoundedIcon
          sx={{
            position: 'absolute',
            bottom: 0,
            right: -2,
            fontSize: 14,
            pointerEvents: 'none',
            color: orange[600],
          }}
        />
      )}
    </Box>
  )
}

HHDateCalendarDay.propTypes = {
  heatmap: PropTypes.object,
  dayMetaByDate: PropTypes.object,
  day: PropTypes.instanceOf(Date),
  outsideCurrentMonth: PropTypes.bool,
}

export default HHDateCalendarDay
